/**
 * Utility Functions
 */

/**
 * Merge class names conditionally (like clsx/classnames)
 */
export function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Format a date relative to now
 */
export function formatRelativeTime(date) {
  if (!date) return '';
  
  const now = new Date();
  const then = new Date(date);
  const diff = now - then;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  
  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (weeks < 4) return `${weeks}w ago`;
  if (months < 12) return `${months}mo ago`;
  return `${years}y ago`;
}

/**
 * Format a date to a readable string
 */
export function formatDate(date, options = {}) {
  if (!date) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return new Date(date).toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format file size to human readable
 */
export function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let size = bytes;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Format a number with abbreviated suffix (1K, 1M, etc.)
 */
export function formatNumber(num) {
  if (!num) return '0';
  
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
}

/**
 * Truncate text to a maximum length
 */
export function truncate(text, maxLength = 100, suffix = '...') {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Debounce a function
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle a function
 */
export function throttle(func, limit = 100) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Generate a unique ID
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Deep clone an object
 */
export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if an object is empty
 */
export function isEmpty(obj) {
  if (!obj) return true;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
}

/**
 * Get file extension
 */
export function getFileExtension(filename) {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

/**
 * Get file icon based on extension
 */
export function getFileIcon(filename) {
  const ext = getFileExtension(filename);
  
  const iconMap = {
    // Images
    jpg: 'Image', jpeg: 'Image', png: 'Image', gif: 'Image', webp: 'Image', svg: 'Image',
    // Documents
    pdf: 'FileText', doc: 'FileText', docx: 'FileText', txt: 'FileText',
    // Code
    js: 'Code', jsx: 'Code', ts: 'Code', tsx: 'Code', py: 'Code', java: 'Code',
    html: 'Code', css: 'Code', json: 'Code',
    // Design
    psd: 'Palette', ai: 'Palette', sketch: 'Palette', fig: 'Palette', xd: 'Palette',
    // 3D
    blend: 'Box', obj: 'Box', stl: 'Box', fbx: 'Box', gltf: 'Box',
    // Audio
    mp3: 'Music', wav: 'Music', ogg: 'Music', flac: 'Music',
    // Video
    mp4: 'Video', mov: 'Video', avi: 'Video', mkv: 'Video',
    // Archive
    zip: 'Archive', rar: 'Archive', '7z': 'Archive', tar: 'Archive'
  };
  
  return iconMap[ext] || 'File';
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }
}

/**
 * Parse tags from various formats
 */
export function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags.map(t => typeof t === 'string' ? t : t.name || '').filter(Boolean);
  }
  if (typeof tags === 'string') {
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Check if two arrays have the same elements
 */
export function arraysEqual(a, b) {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
}

/**
 * Safely get nested property
 */
export function get(obj, path, defaultValue = undefined) {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result === null || result === undefined) {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result === undefined ? defaultValue : result;
}

/**
 * Convert camelCase to kebab-case
 */
export function toKebabCase(str) {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Convert snake_case to camelCase
 */
export function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Normalize project data (handle different API response formats)
 */
export function normalizeProject(project) {
  if (!project) return null;
  
  return {
    id: project.id,
    name: project.name,
    description: project.description,
    category: project.category,
    projectType: project.project_type || project.projectType || 'digital',
    imageUrl: project.image_url || project.imageUrl || project.coverImage,
    files: project.files || [],
    tags: parseTags(project.tags),
    userId: project.user_id || project.userId,
    author: project.author,
    parentProjectId: project.parent_project_id || project.parentProjectId,
    parentProjectIds: project.parent_project_ids || project.parentProjectIds || [],
    forkCount: project.fork_count || project.forkCount || 0,
    viewCount: project.view_count || project.viewCount || 0,
    starCount: project.star_count || project.starCount || 0,
    commentCount: project.comment_count || project.commentCount || 0,
    status: project.status || 'in_progress',
    progress: project.progress || 0,
    isPublic: project.is_public !== false,
    isChallengeResponse: project.is_challenge_response || project.isChallengeResponse,
    challengeId: project.challenge_id || project.challengeId,
    createdAt: project.created_at || project.createdAt,
    updatedAt: project.updated_at || project.updatedAt
  };
}

/**
 * Normalize challenge data
 */
export function normalizeChallenge(challenge) {
  if (!challenge) return null;
  
  return {
    id: challenge.id,
    title: challenge.challenge_title || challenge.challengeTitle || challenge.title,
    description: challenge.challenge_description || challenge.challengeDescription || challenge.description,
    projectId: challenge.project_id || challenge.projectId,
    userId: challenge.user_id || challenge.userId || challenge.requester_id || challenge.requesterId,
    status: challenge.status || 'open',
    responseCount: challenge.response_count || challenge.responseCount || 0,
    createdAt: challenge.created_at || challenge.createdAt,
    author: challenge.requester || challenge.author
  };
}
